import PropTypes from "prop-types"
import React, { useState, useEffect, useRef } from "react"
import SimpleBar from "simplebar-react"
import MetisMenu from "metismenujs"
import { Link, useLocation } from "react-router-dom"
import { withTranslation } from "react-i18next"
import { usePermissions } from "API/PermissionContext"

const SidebarContent = props => {
  const ref = useRef()
  const location = useLocation()
  const { canView, canEdit, canExport, canApprove, MODULE, loaded } = usePermissions()

  // Track MetisMenu init count for debugging
  const mmInitCount = useRef(0)
  const mmInstance = useRef(null)

  useEffect(() => {
    // ★ KEY FIX: Only init MetisMenu AFTER permissions are loaded
    // Before this fix, MetisMenu was initialized at loaded=false AND loaded=true,
    // creating duplicate event handlers that fought each other (open→close on same click)
    if (!loaded) {
      console.log(`%c⏳ SIDEBAR: Skipping MetisMenu init (permissions not loaded yet)`, 'color: #6b7280')
      return
    }

    const initMenu = () => {
      mmInitCount.current++
      const el = document.getElementById("side-menu")
      if (!el) {
        console.warn("🔴 SIDEBAR: #side-menu not found in DOM")
        return
      }

      const subMenus = el.querySelectorAll(".sub-menu")
      const hasArrows = el.querySelectorAll(".has-arrow")
      console.log(`%c🔵 SIDEBAR: MetisMenu init #${mmInitCount.current} | loaded=${loaded} | path=${location.pathname} | subMenus=${subMenus.length} | hasArrow=${hasArrows.length}`, 'color: #3b82f6')

      // Dispose previous MetisMenu instance if exists
      if (mmInstance.current && typeof mmInstance.current.dispose === 'function') {
        try { mmInstance.current.dispose() } catch (e) { /* ignore */ }
      }

      // Clean up old MetisMenu state
      el.querySelectorAll(".mm-collapse").forEach(e => e.classList.remove("mm-collapse"))
      el.querySelectorAll(".mm-show").forEach(e => e.classList.remove("mm-show"))
      el.querySelectorAll(".mm-active").forEach(e => e.classList.remove("mm-active"))
      el.querySelectorAll(".mm-collapsing").forEach(e => e.classList.remove("mm-collapsing"))

      try {
        mmInstance.current = new MetisMenu("#side-menu")
        console.log(`%c✅ SIDEBAR: MetisMenu created OK`, 'color: #22c55e')
      } catch (err) {
        console.error("❌ SIDEBAR: MetisMenu init FAILED:", err)
      }

      // Activate current page's menu item
      let matchingMenuItem = null
      const items = el.getElementsByTagName("a")
      for (let i = 0; i < items.length; ++i) {
        if (location.pathname === items[i].pathname) {
          matchingMenuItem = items[i]
          break
        }
      }
      if (matchingMenuItem) {
        console.log(`%c🟢 SIDEBAR: Active item found: ${matchingMenuItem.textContent.trim()}`, 'color: #22c55e')
        activateParentDropdown(matchingMenuItem)
      }
    }
    initMenu()
  }, [location.pathname, loaded])

  useEffect(() => {
    if (ref.current) ref.current.recalculate()
  }, [location.pathname, loaded])

  function scrollElement(item) {
    if (item) {
      const currentPosition = item.offsetTop
      if (currentPosition > window.innerHeight) {
        ref.current.getScrollElement().scrollTop = currentPosition - 300
      }
    }
  }

  function activateParentDropdown(item) {
    item.classList.add("active")
    const parent = item.parentElement
    const parent2El = parent.childNodes[1]
    if (parent2El && parent2El.id !== "side-menu") parent2El.classList.add("mm-show")
    if (parent) {
      parent.classList.add("mm-active")
      const parent2 = parent.parentElement
      if (parent2) {
        parent2.classList.add("mm-show")
        const parent3 = parent2.parentElement
        if (parent3) {
          parent3.classList.add("mm-active")
          parent3.childNodes[0].classList.add("mm-active")
          const parent4 = parent3.parentElement
          if (parent4) {
            parent4.classList.add("mm-show")
            const parent5 = parent4.parentElement
            if (parent5) {
              parent5.classList.add("mm-show")
              parent5.childNodes[0].classList.add("mm-active")
            }
          }
        }
      }
      scrollElement(item)
      return false
    }
    scrollElement(item)
    return false
  }

  // Helper: show parent menu only if at least one child is visible
  const M = MODULE
  const showMasters = canView(M.COMPANY) || canView(M.PLANTS) || canView(M.CHILLING_CENTERS) || canView(M.ROUTES) || canView(M.COLLECTION_CENTERS) || canView(M.BULK_VENDORS) || canView(M.PRODUCERS) || canView(M.MATERIALS) || canView(M.STAFFS) || canView(M.TRANSPORTERS) || canView(M.VEHICLES)
  const showIncentives = canView(M.INCENTIVE_CHARTS) || canView(M.CC_INCENTIVES) || canView(M.VLCC_INCENTIVES)
  const showProcurement = canView(M.VLCC_PURCHASES) || canView(M.CC_PURCHASES) || canView(M.CC_SPOILED) || canView(M.CENTER_MILK_SALES) || canView(M.MILK_TRANSFERS)
  const showSession = canView(M.SESSION_STATUS)
  const showBills = canView(M.ROUTE_BILLS) || canView(M.TRANSPORT_BILLS) || canView(M.BONUS_BILLS)
  const showAdvances = canView(M.VENDOR_LOANS) || canView(M.VENDOR_CASH_ADV) || canView(M.VENDOR_MATERIAL_ADV) || canView(M.PRODUCER_LOANS) || canView(M.PRODUCER_CASH_ADV) || canView(M.PRODUCER_MATERIAL_ADV) || canView(M.VEHICLE_CASH_ADV)
  const showRecoveries = canView(M.VENDOR_RECOVERIES) || canView(M.PRODUCER_RECOVERIES) || canView(M.VEHICLES)
  const showConfig = canView(M.CONFIGURATIONS) || canView(M.ROLES_USERS) || canView(M.SMS_SETTINGS)
  const showCustomization = canView(M.BILL_CUSTOMIZATION)
  const showReports = canView(M.REPORTS)
  const showGraphReports = canView(M.GRAPH_REPORTS)

  return (
    <>
      <React.Fragment>
        <SimpleBar style={{ maxHeight: "100%" }} ref={ref}>
          <div id="sidebar-menu">
            <ul className="metismenu list-unstyled" id="side-menu">
              <li className="menu-title">{props.t("Main Navigation")}</li>

              {/* Dashboard */}
              {canView(M.DASHBOARD) && (
                <li>
                  <Link to="/dashboard" className="waves-effect">
                    <i className="fas fa-columns"></i>
                    <span>{props.t("Dashboard")}</span>
                  </Link>
                </li>
              )}

              {/* Configurations */}
              {showConfig && (
                <li>
                  <Link to="/#" className="has-arrow waves-effect">
                    <i className="fas fa-screwdriver"></i>
                    <span>{props.t("Configurations")}</span>
                  </Link>
                  <ul className="sub-menu" aria-expanded="false">
                    {canView(M.CONFIGURATIONS) && (
                      <>
                        <li><Link to="/material-unit-type">{props.t("Material Unit Types")}</Link></li>
                        <li><Link to="/can-types">{props.t("Can Types")}</Link></li>
                        <li><Link to="/designations">{props.t("Designations")}</Link></li>
                        <li><Link to="/fundtypes">{props.t("Fund Types")}</Link></li>
                        <li><Link to="/incentive-types">{props.t("Additional Incentive Types")}</Link></li>
                        <li><Link to="/service-types">{props.t("Service Types")}</Link></li>
                        <li><Link to="/service-items">{props.t("Service Items")}</Link></li>
                      </>
                    )}

                    {canView(M.SMS_SETTINGS) && (
                      <li><Link to="/sms-settings">{props.t("SMS & WhatsApp Settings")}</Link></li>
                    )}
                    {canView(M.ROLES_USERS) && (
                      <>
                        <li><Link to="/roles">{props.t("Roles")}</Link></li>
                        <li><Link to="/users">{props.t("Users")}</Link></li>
                      </>
                    )}
                  </ul>
                </li>
              )}

              {/* Customization */}
              {showCustomization && (
                <li>
                  <Link to="/#" className="has-arrow waves-effect">
                    <i className="fas fa-sliders-h"></i>
                    <span>{props.t("Customization")}</span>
                  </Link>
                  <ul className="sub-menu" aria-expanded="false">
                    <li><Link to="/bill-customization">{props.t("Bill Customization")}</Link></li>
                  </ul>
                </li>
              )}

              {/* Map View */}
              {canView(M.MAP_VIEW) && (
                <li>
                  <Link to="/map-view" className="waves-effect">
                    <i className="fas fa-map-marked-alt"></i>
                    <span>{props.t("Map View")}</span>
                  </Link>
                </li>
              )}

              {/* Rate Charts */}
              {canView(M.RATE_CHARTS) && (
                <li>
                  <Link to="/rate-charts" className="waves-effect">
                    <i className="fas fa-chart-line"></i>
                    <span>{props.t("Rate Charts")}</span>
                  </Link>
                </li>
              )}

              {/* Incentives */}
              {showIncentives && (
                <li>
                  <Link to="/#" className="has-arrow waves-effect">
                    <i className="fas fa-coins"></i>
                    <span>{props.t("Incentives")}</span>
                  </Link>
                  <ul className="sub-menu" aria-expanded="false">
                    {canView(M.INCENTIVE_CHARTS) && <li><Link to="/incentive-charts">{props.t("Incentive Charts")}</Link></li>}
                    {canView(M.CC_INCENTIVES) && <li><Link to="/cc-incentives">{props.t("CC Incentives")}</Link></li>}
                    {canView(M.VLCC_INCENTIVES) && <li><Link to="/vlcc-incentives">{props.t("VLCC Incentives")}</Link></li>}
                  </ul>
                </li>
              )}

              {/* Procurement */}
              {showProcurement && (
                <li>
                  <Link to="/#" className="has-arrow waves-effect">
                    <i className="fas fa-funnel-dollar"></i>
                    <span>{props.t("Procurement")}</span>
                  </Link>
                  <ul className="sub-menu" aria-expanded="false">
                    {canView(M.VLCC_PURCHASES) && <li><Link to="/vlcc-purchases">{props.t("VLCC Purchases")}</Link></li>}
                    {canView(M.CC_PURCHASES) && <li><Link to="/cc-purchases">{props.t("CC Purchases")}</Link></li>}
                    {canView(M.CC_SPOILED) && <li><Link to="/cc-spoiled-purchases">{props.t("CC Spoiled Purchases")}</Link></li>}
                    {canView(M.CENTER_MILK_SALES) && <li><Link to="/center-milk-sales">{props.t("Center Milk Sales")}</Link></li>}
                    {canView(M.MILK_TRANSFERS) && <li><Link to="/milk-transfers">{props.t("Milk Transfers")}</Link></li>}
                  </ul>
                </li>
              )}

              {/* Session Status */}
              {showSession && (
                <li>
                  <Link to="/#" className="has-arrow waves-effect">
                    <i className="fas fa-calendar-check"></i>
                    <span>{props.t("Session Status")}</span>
                  </Link>
                  <ul className="sub-menu" aria-expanded="false">
                    <li><Link to="/vlcc">{props.t("VLCC")}</Link></li>
                    <li><Link to="/cc">{props.t("CC")}</Link></li>
                  </ul>
                </li>
              )}

              {/* Bills */}
              {showBills && (
                <li>
                  <Link to="/#" className="has-arrow waves-effect">
                    <i className="fas fa-rupee-sign"></i>
                    <span>{props.t("Bills")}</span>
                  </Link>
                  <ul className="sub-menu" aria-expanded="false">
                    {canView(M.ROUTE_BILLS) && <li><Link to="/bills">{props.t("Procurement Bills")}</Link></li>}
                    {canView(M.TRANSPORT_BILLS) && <li><Link to="/transport-bills">{props.t("Transport Bills")}</Link></li>}
                    {canView(M.ROUTE_BILLS) && <li><Link to="/vlcc">{props.t("Procurement Funds")}</Link></li>}
                    {canView(M.BONUS_BILLS) && <li><Link to="/vlcc-bonus-bills">{props.t("VLCC Bonus Bills")}</Link></li>}
                  </ul>
                </li>
              )}

              {/* Payment System */}
              {canView(M.PAYMENTS) && (
                <li>
                  <Link to="/#" className="has-arrow waves-effect">
                    <i className="fas fa-money-check-alt"></i>
                    <span>{props.t("Payments")}</span>
                  </Link>
                  <ul className="sub-menu" aria-expanded="false">
                    <li><Link to="/payment-dashboard">{props.t("Payment Dashboard")}</Link></li>
                    {canApprove(M.PAYMENTS) && <li><Link to="/payment-process">{props.t("Process Payment")}</Link></li>}
                    {canExport(M.PAYMENTS) && <li><Link to="/payment-ledger">{props.t("Payment Ledger")}</Link></li>}
                    {canEdit(M.PAYMENTS) && <li><Link to="/payment-config">{props.t("Payment Settings")}</Link></li>}
                  </ul>
                </li>
              )}

              {/* Advances */}
              {showAdvances && (
                <li>
                  <Link to="/#" className="has-arrow waves-effect">
                    <i className="fas fa-rupee-sign"></i>
                    <span>{props.t("Advances")}</span>
                  </Link>
                  <ul className="sub-menu">
                    {canView(M.VENDOR_LOANS) && <li><Link to="/vendor-loans">{props.t("Vendor Loans")}</Link></li>}
                    {canView(M.VENDOR_CASH_ADV) && <li><Link to="/vendor-cash-advances">{props.t("Vendor Cash Advances")}</Link></li>}
                    {canView(M.VENDOR_MATERIAL_ADV) && <li><Link to="/vendor-material-advances">{props.t("Vendor Material Advances")}</Link></li>}
                    {canView(M.PRODUCER_LOANS) && <li><Link to="/producer-loans">{props.t("Producer Loans")}</Link></li>}
                    {canView(M.PRODUCER_CASH_ADV) && <li><Link to="/producer-cash-advances">{props.t("Producer Cash Advances")}</Link></li>}
                    {canView(M.PRODUCER_MATERIAL_ADV) && <li><Link to="/producer-material-advances">{props.t("Producer Material Advances")}</Link></li>}
                    {canView(M.VEHICLE_CASH_ADV) && <li><Link to="/vehicle-cash-advances">{props.t("Vehicle Cash Advances")}</Link></li>}
                  </ul>
                </li>
              )}

              {/* Recoveries */}
              {showRecoveries && (
                <li>
                  <Link to="/#" className="has-arrow waves-effect">
                    <i className="fas fa-hand-holding-usd"></i>
                    <span>{props.t("Recoveries")}</span>
                  </Link>
                  <ul className="sub-menu" aria-expanded="false">
                    {canView(M.VENDOR_RECOVERIES) && <li><Link to="/vendor-recoveries">{props.t("Vendor Recoveries")}</Link></li>}
                    {canView(M.VENDOR_RECOVERIES) && <li><Link to="/vendor-additions">{props.t("Vendor Additions")}</Link></li>}
                    {canView(M.PRODUCER_RECOVERIES) && <li><Link to="/producer-recoveries">{props.t("Producer Recoveries")}</Link></li>}
                    {canView(M.PRODUCER_RECOVERIES) && <li><Link to="/producer-additions">{props.t("Producer Additions")}</Link></li>}
                    {canView(M.VEHICLES) && <li><Link to="/client-vehicle-recoveries">{props.t("Client Vehicle Recoveries")}</Link></li>}
                    {canView(M.VEHICLES) && <li><Link to="/client-vehicle-additions">{props.t("Client Vehicle Additions")}</Link></li>}
                  </ul>
                </li>
              )}

              {/* Masters */}
              {showMasters && (
                <li>
                  <Link to="/#" className="has-arrow waves-effect">
                    <i className="fas fa-database"></i>
                    <span>{props.t("Masters")}</span>
                  </Link>
                  <ul className="sub-menu" aria-expanded="false">
                    {canView(M.COMPANY) && <li><Link to="/companies">{props.t("Companies")}</Link></li>}
                    {canView(M.PLANTS) && <li><Link to="/plants">{props.t("Plants")}</Link></li>}
                    {canView(M.CHILLING_CENTERS) && <li><Link to="/chilling-centers">{props.t("Chilling Centers")}</Link></li>}
                    {canView(M.ROUTES) && <li><Link to="/routes">{props.t("Routes")}</Link></li>}
                    {canView(M.COLLECTION_CENTERS) && <li><Link to="/collection-center-vlcc">{props.t("Collection Centers (VLCC)")}</Link></li>}
                    {canView(M.BULK_VENDORS) && <li><Link to="/bulk-vendors">{props.t("Bulk Vendors")}</Link></li>}
                    {canView(M.BULK_VENDORS) && <li><Link to="/business-vendors">{props.t("Business Vendors")}</Link></li>}
                    {canView(M.PRODUCERS) && <li><Link to="/producers">{props.t("Producers")}</Link></li>}
                    {canView(M.MATERIALS) && <li><Link to="/materials">{props.t("Materials")}</Link></li>}
                    {canView(M.STAFFS) && <li><Link to="/staffs">{props.t("Staffs")}</Link></li>}
                    {canView(M.TRANSPORTERS) && <li><Link to="/transporters">{props.t("Transporters")}</Link></li>}
                    {canView(M.VEHICLES) && <li><Link to="/additions-deductions">{props.t("Additions/Deductions")}</Link></li>}
                  </ul>
                </li>
              )}

              {/* Reports */}
              {showReports && (
                <li>
                  <Link to="/#" className="has-arrow waves-effect">
                    <i className="fas fa-table"></i>
                    <span>{props.t("Reports")}</span>
                  </Link>
                  <ul className="sub-menu" aria-expanded="false">
                    <li><Link to="/procurement-summary">{props.t("Procurement Summary")}</Link></li>
                    <li><Link to="/cc-vlcc-comparision">{props.t("CC Vs VLCC Comparison")}</Link></li>
                    <li><Link to="/procurement-bill-summary">{props.t("Procurement Bill Summary")}</Link></li>
                    <li><Link to="/vendor-advances-report">{props.t("Vendor Advances")}</Link></li>
                    <li><Link to="/producer-advances-report">{props.t("Producer Advances")}</Link></li>
                    <li><Link to="/procurement-attendance">{props.t("Procurement Attendance")}</Link></li>
                    <li><Link to="/sms-report">{props.t("SMS Report")}</Link></li>
                    <li><Link to="/vlcc-settings-report">{props.t("VLCC Settings Report")}</Link></li>
                    <li><Link to="/cleaning-report">{props.t("Cleaning Report")}</Link></li>
                    {/* <li><Link to="/vlcc-absentees">{props.t("VLCC Absentees")}</Link></li> */}
                    {/* <li><Link to="/producer-performance">{props.t("Producer Performance")}</Link></li> */}
                    {/* <li><Link to="/route-collection">{props.t("Route Collection")}</Link></li> */}
                    {/* <li><Link to="/advance-summary">{props.t("Advance & Loan Summary")}</Link></li> */}
                    {/* <li><Link to="/payment-report">{props.t("Payment Report")}</Link></li> */}
                    <li><Link to="/funds-report">{props.t("Funds Report")}</Link></li>
                    <li><Link to="/vlcc-ratechart-status">{props.t("Rate Chart Status")}</Link></li>
                    <li><Link to="/producer-report">{props.t("Producer Report")}</Link></li>
                    <li><Link to="/milk-transfer-report">{props.t("Milk Transfer Report")}</Link></li>
                  </ul>
                </li>
              )}

              {/* Graph Reports */}
              {showGraphReports && (
                <li>
                  <Link to="/#" className="has-arrow waves-effect">
                    <i className="fas fa-chart-pie"></i>
                    <span>{props.t("Graph Reports")}</span>
                  </Link>
                  <ul className="sub-menu" aria-expanded="false">
                    <li><Link to="/procurement-history">{props.t("Procurement History")}</Link></li>
                    <li><Link to="/quality-analysis">{props.t("Quality Analysis")}</Link></li>
                    <li><Link to="/cc-vs-vlcc">{props.t("CC vs VLCC Comparison")}</Link></li>
                  </ul>
                </li>
              )}
            </ul>
          </div>
        </SimpleBar>
      </React.Fragment>
    </>
  )
}

SidebarContent.propTypes = {
  location: PropTypes.object,
  t: PropTypes.any,
}

export default withTranslation()(SidebarContent)